/**
 * FuarSeriesListPage.jsx — Fuar Serileri
 * Route: /Management/Fairs/Series
 *
 * Bir seri, her yıl yeniden açılan fuarın kalıcı kimliği ("EMITT" → EMITT 2025, 2026 …):
 * bağlantı + dil başına ad ve hikâye. Fuarlar seriye Fuar Düzenle ekranından bağlanır.
 *
 * CANLI: `GET /fuar/series/list?languageId=1|2` (TR ve EN birleştirilerek),
 * `POST /fuar/series`, `PUT /fuar/series/{id}`, `DELETE /fuar/series/delete-soft/{id}`.
 * Oluştur/düzenle modalı paylaşımlı: Components/Fairs/FuarSeriesModal.jsx.
 *
 * ⚠️ Silme YUMUŞAK ve bağlı fuarları ayırmaz — fuarlar fuar_series_id'lerini korur,
 *    seri yalnızca seçim listelerinden düşer. Fuar listesi uç noktası (FuarListDTO)
 *    fuarSeriesId taşımadığı için burada "bu seriye bağlı fuarlar" gösterilemiyor.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Container, Card, CardBody, CardHeader, Row, Col, Button, Input, Table, Badge,
  Spinner, Alert, Modal, ModalHeader, ModalBody, ModalFooter,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import FuarSeriesModal from "../../../Components/Fairs/FuarSeriesModal";
import { fuarService, apiErrorMessage } from "../../../services/fuarService";
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n";

const normalize = (s) => (s || "").toLocaleLowerCase("tr-TR");

export default function FuarSeriesListPage() {
  const { t: tx } = useTranslation("yonetim");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [search, setSearch] = useState("");
  const [reloadToken, setReloadToken] = useState(0);
  const [modal, setModal] = useState(null); // null | { seriesId, initial }
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { document.title = i18n.t("yonetim:fuarSeriesList.fuarSerileriFds"); }, []);

  useEffect(() => {
    if (!successMsg) return undefined;
    const t = setTimeout(() => setSuccessMsg(null), 3500);
    return () => clearTimeout(t);
  }, [successMsg]);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    let ignore = false;
    async function run() {
      setLoading(true);
      setError(null);
      try {
        const list = await fuarService.listFuarSeriesMerged();
        if (!ignore) setRows(list);
      } catch (e) {
        if (!ignore) setError(apiErrorMessage(e, i18n.t("yonetim:fuarSeriesList.serilerYuklenemedi")));
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    run();
    return () => { ignore = true; };
  }, [reloadToken]);

  const filtered = useMemo(() => {
    const q = normalize(search.trim());
    if (!q) return rows;
    return rows.filter((r) => [r.nameTr, r.nameEn, r.link, String(r.id)].some((v) => normalize(v).includes(q)));
  }, [rows, search]);

  function handleSaved(_id, msg) {
    setModal(null);
    setSuccessMsg(msg);
    setReloadToken((n) => n + 1);
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await fuarService.deleteFuarSeries(deleteTarget.id);
      setSuccessMsg(tx("fuarSeriesList.silindi", { v: deleteTarget.nameTr || tx("fuarSeriesList.seri", { id: deleteTarget.id }) }));
      setDeleteTarget(null);
      setReloadToken((n) => n + 1);
    } catch (e) {
      setError(apiErrorMessage(e, tx("fuarSeriesList.seriSilinemedi")));
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={tx("fuarSeriesList.fuarSerileri")} pageTitle={tx("fuarSeriesList.fuarlar")} />

        {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}
        {successMsg && <Alert color="success">{successMsg}</Alert>}

        <Card className="mb-3">
          <CardBody className="py-3">
            <Row className="g-2 align-items-center">
              <Col md={5}>
                <div className="search-box">
                  <Input
                    className="form-control search" placeholder={tx("fuarSeriesList.seriAdiBaglantiVeyaId")}
                    value={search} onChange={(e) => setSearch(e.target.value)}
                  />
                  <i className="ri-search-line search-icon" />
                </div>
              </Col>
              <Col md={7} className="d-flex gap-2 justify-content-md-end">
                <Link to="/Management/Fairs" className="btn btn-light">
                  <i className="bx bx-arrow-back me-1" />{tx("fuarSeriesList.fuarListesi")}
                </Link>
                <Button color="secondary" outline onClick={() => setReloadToken((n) => n + 1)} disabled={loading}>
                  <i className="bx bx-refresh me-1" />{tx("common:yenile")}
                </Button>
                <Button color="primary" onClick={() => setModal({ seriesId: null, initial: null })}>
                  <i className="bx bx-plus me-1" />{tx("fuarSeriesList.yeniSeri")}
                </Button>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <Card>
          <CardHeader className="d-flex align-items-center gap-2">
            <i className="bx bx-collection text-primary fs-5" />
            <span className="fw-semibold">{tx("fuarSeriesList.seriler")}</span>
            <Badge color="light" className="text-dark">{filtered.length}</Badge>
          </CardHeader>
          <CardBody>
            {loading ? (
              <div className="text-center py-5"><Spinner color="primary" /></div>
            ) : filtered.length === 0 ? (
              <Alert color="secondary" className="mb-0">
                {rows.length === 0 ? tx("fuarSeriesList.henuzTanimliSeriYok") : tx("fuarSeriesList.aramayaUyanSeriYok")}
              </Alert>
            ) : (
              <div className="table-responsive">
                <Table className="align-middle table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 70 }}>#</th>
                      <th>{tx("fuarSeriesList.seriAdiTr")}</th>
                      <th>{tx("fuarSeriesList.seriesNameEn")}</th>
                      <th>{tx("fuarSeriesList.baglanti")}</th>
                      <th className="text-end" style={{ width: 120 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((r) => (
                      <tr key={r.id}>
                        <td className="text-muted">{r.id}</td>
                        <td className="fw-semibold">{r.nameTr || <span className="text-muted fst-italic">{tx("fuarSeriesList.adsiz")}</span>}</td>
                        <td>{r.nameEn || <span className="text-muted">—</span>}</td>
                        <td className="small">
                          {r.link ? (
                            <a href={r.link} target="_blank" rel="noreferrer" className="text-break">
                              <i className="bx bx-link-external me-1" />{r.link}
                            </a>
                          ) : <span className="text-muted">—</span>}
                        </td>
                        <td className="text-end">
                          <Button
                            size="sm" color="secondary" outline className="me-1" title={tx("common:duzenle")}
                            onClick={() => setModal({ seriesId: r.id, initial: r })}
                          >
                            <i className="bx bx-edit" />
                          </Button>
                          <Button size="sm" color="danger" outline title={tx("common:sil")} onClick={() => setDeleteTarget(r)}>
                            <i className="bx bx-trash" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </CardBody>
        </Card>

        <FuarSeriesModal
          isOpen={Boolean(modal)}
          toggle={() => setModal(null)}
          seriesId={modal?.seriesId ?? null}
          initial={modal?.initial ?? null}
          onSaved={handleSaved}
        />

        <Modal isOpen={Boolean(deleteTarget)} toggle={() => !deleting && setDeleteTarget(null)} centered>
          <ModalHeader toggle={() => !deleting && setDeleteTarget(null)}>{tx("fuarSeriesList.seriyiSil")}</ModalHeader>
          <ModalBody>
            <p className="mb-2">
              <span className="fw-semibold">{deleteTarget?.nameTr || tx("fuarSeriesList.seri", { id: deleteTarget?.id })}</span>{" "}{tx("fuarSeriesList.silinecek")}
            </p>
            <p className="text-muted small mb-0">
              {tx("fuarSeriesList.buSeriyeBagliFuarlar")}{" "}<span className="fw-semibold">{tx("fuarSeriesList.bagliKalir")}</span>{tx("fuarSeriesList.seriYalnizcaSecimListelerindenKaldirilir")}
            </p>
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={() => setDeleteTarget(null)} disabled={deleting}>{tx("fuarSeriesList.vazgec")}</Button>
            <Button color="danger" onClick={handleDelete} disabled={deleting}>
              {deleting ? <Spinner size="sm" className="me-1" /> : <i className="bx bx-trash me-1" />}{tx("common:sil")}
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
}
